import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectEmrSectionsData,
  setEmrSectionsData,
} from 'src/clinic/reducers/prescription/prescriptionReducer'
import { fetchEmrSections } from 'src/clinic/reducers/prescription/services/emrSectionThunk'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessful,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams, postMPDataWithoutParams } from 'src/mentor/services'

/**
 * Custom hook for managing EMR sections data and logic
 * @param {Array} currentPageHistory - History of current pages for navigation
 * @returns {Object} EMR sections data and functions
 */
export const useEmrSections = (currentPageHistory = [], reportType, options = {}) => {
  // `hasScreeningReportData` lets the tablet (DocOn) layout drop the screening-report slot
  // when this patient has none, so the next real section is pulled into the freed column
  // instead of showing an empty card. Defaults to true so other callers are unaffected.
  const { hasScreeningReportData = true } = options
  const dispatch = useDispatch()

  // Redux selectors — read synchronously so a warm cache is available at first render.
  const emrSectionsStoreData = useSelector(selectEmrSectionsData)

  const [showNextVisitSlotSelector, setShowNextVisitSlotSelector] = useState(false)
  // Lazy-initialise from the (doctor-wide, Redux-persisted) cache so the editor sections paint on
  // the FIRST render when the config is already warm — instead of starting empty and only filling
  // in after the post-paint useEffect below. This is what stops the "every section loads again"
  // flash when moving between appointments.
  const [emrSections, setEmrSections] = useState(
    () => emrSectionsStoreData?.data?.[reportType]?.emrSections || [],
  )
  const [saveLoading, setSaveLoading] = useState(false)

  // // Basic EMR sections data
  // const emrSections = useMemo(() => {
  //   return emrSectionsStoreData?.data || []
  // }, [emrSectionsStoreData])

  const loadingEmrSections = useMemo(() => {
    return emrSectionsStoreData?.state === 'loading'
  }, [emrSectionsStoreData])

  // Current page calculation
  const currentPage = useMemo(() => {
    return currentPageHistory?.[currentPageHistory.length - 1] || 0
  }, [currentPageHistory])

  // EMR sections for regular editor view (filtered by selected and editor-visible)
  const emrSectionsToShowInOrder = useMemo(() => {
    return emrSections.filter((item) => item?.selected && item?.printOnly !== true)
  }, [emrSections])

  // EMR sections for DocOn layout (with special filtering and finishing details)
  const emrSectionsToShowInOrderForDocOn = useMemo(() => {
    let sections = emrSections
      .filter((item) => item?.selected && item?.printOnly !== true)
      .filter(
        (item) =>
          item?.id !== 'next_visit_details' &&
          item?.id !== 'therapy_recommendation' &&
          item?.id !== 'payment_and_refund',
      )
      // Drop the screening-report slot when there's no report to show — otherwise it renders
      // as an empty column. Removing it here lets pagination pull in the next real section.
      .filter((item) => {
        const isScreeningReport =
          item?.id === 'screening-report' ||
          item?.id === 'screeningReportData' ||
          item?.defCode === 'screeningReportData'
        return isScreeningReport ? hasScreeningReportData : true
      })

    // Add finishing details section if any of the finishing sections exist
    if (
      ['therapy_recommendation', 'payment_and_refund', 'next_visit_details'].some((id) =>
        emrSections.map((item) => item?.id).includes(id),
      )
    ) {
      sections = [...sections, { id: 'finshing_details', enabled: true, title: 'Finishing Up' }]
    }

    // Always add prescription section at the end
    sections = [
      ...sections,
      { id: 'prescription', enabled: true, title: 'View/Print Prescription' },
    ]

    return sections
  }, [emrSections, hasScreeningReportData])

  // Check if currently on prescription preview page
  const onPrescriptionPreviewPage = useMemo(() => {
    const prescriptionIndex = emrSectionsToShowInOrderForDocOn.findIndex(
      (item) => item?.id === 'prescription',
    )
    return currentPage === prescriptionIndex || currentPage + 1 === prescriptionIndex
  }, [currentPage, emrSectionsToShowInOrderForDocOn])

  /**
   * Fetch EMR sections from API
   */
  const getEmrSections = useCallback(async () => {
    dispatch(fetchEmrSections())
  }, [dispatch])

  /**
   * Fetch EMR meta data (next visit widget information)
   */
  const getEMRMeta = useCallback(async () => {
    try {
      const response = await getMPDataWithoutParams(`/api/secure/teacher/next-visit-widget/get`)
      if (response.status === 200) {
        if (isCommonResponseSuccessfulV2(response)) {
          const respData = getDataMapFromCommonResponse(response)?.['emrMeta']
          const shouldShowNextVisitSlotSelector = respData?.nextVisitHoldSlot?.doctors
          setShowNextVisitSlotSelector(shouldShowNextVisitSlotSelector)
          return respData
        }
      }
      return null
    } catch (error) {
      console.error('Error fetching EMR meta:', error)
      return null
    }
  }, [])

  /**
   * Initialize EMR sections and meta data
   */
  const initializeEmrData = useCallback(async () => {
    await Promise.all([getEmrSections(), getEMRMeta()])
  }, [getEmrSections, getEMRMeta])

  /**
   * Handle EMR section enable/disable toggle
   */
  const handleClickEmrSection = useCallback(async (emrSectionItem, checked) => {
    setEmrSections((prevSections) => {
      return prevSections.map((section) => {
        if (section?.id === emrSectionItem?.id) {
          return { ...section, selected: checked }
        }
        return section
      })
    })
  }, [])

  /**
   * Toggle EMR section printable status
   */
  const toggleEmrSectionPrintable = useCallback(async (emrSectionItem) => {
    setEmrSections((prevSections) => {
      return prevSections.map((section) => {
        if (section?.id === emrSectionItem?.id) {
          return { ...section, printable: !section?.printable }
        }
        return section
      })
    })
  }, [])

  /**
   * Refresh EMR sections on the report page.
   * When syncedData is provided (saved map from EMR Section Editor), updates Redux immediately
   * so the report page re-renders without refetch/flicker. Otherwise fetches from API.
   * @param {Object} [syncedData] - Full EMR settings map by report type (e.g. { prescription: { emrSections }, 'assessment-report': { emrSections } })
   */
  function refreshEmrSections(syncedData) {
    if (syncedData && typeof syncedData === 'object' && Object.keys(syncedData).length > 0) {
      // optimistic: push the just-saved map so the page updates instantly...
      dispatch(setEmrSectionsData(syncedData))
      // ...then reconcile with the server in the background — it resolves
      // subsection selections / effective layouts the local map may not carry
      dispatch(fetchEmrSections({ forceUpdateSections: true }))
      return
    }
    dispatch(fetchEmrSections({ forceUpdateSections: true }))
  }

  /**
   * Save EMR section preferences
   */
  const saveEmrSectionPreference = useCallback(
    async (
      emrSettingsSaveApiSignature = '/api/secure/practitioner/emr-settings/emr-sections/save',
    ) => {
      setSaveLoading(true)
      const data = [...emrSections]
      const response = await postMPDataWithoutParams(emrSettingsSaveApiSignature, data)
      setSaveLoading(false)
      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        // Force refresh by clearing existing data first, then fetching fresh data
        return true
      }
    },
    [emrSections],
  )

  /**
   * Handle drag and drop reordering
   */
  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return // Dropped outside the list

      const newOrder = Array.from(emrSections)
      const [movedItem] = newOrder.splice(result.source.index, 1)
      newOrder.splice(result.destination.index, 0, movedItem)

      setEmrSections(newOrder)
    },
    [emrSections],
  )

  function updateEmrSectionPrintStatus(sectionId, printStatus) {
    setEmrSections((prev) => {
      return prev.map((item) => {
        if (item?.id === sectionId) {
          return { ...item, printable: printStatus }
        }
        return item
      })
    })
  }

  useEffect(() => {
    if (emrSectionsStoreData?.data) {
      const data = emrSectionsStoreData?.data
      setEmrSections(data?.[reportType]?.emrSections || [])
    }
  }, [emrSectionsStoreData, reportType])

  return {
    // Basic EMR sections data
    emrSections,
    setEmrSections,
    loadingEmrSections,
    saveLoading,

    // Computed section arrays
    emrSectionsToShowInOrder,
    emrSectionsToShowInOrderForDocOn,

    // Page navigation
    currentPage,
    onPrescriptionPreviewPage,

    // Next visit slot selector
    showNextVisitSlotSelector,
    setShowNextVisitSlotSelector,

    // Functions
    getEmrSections,
    getEMRMeta,
    initializeEmrData,
    handleClickEmrSection,
    toggleEmrSectionPrintable,
    saveEmrSectionPreference,
    saveEmrSections: saveEmrSectionPreference,
    handleDragEnd,
    refreshEmrSections,
    updateEmrSectionPrintStatus,
  }
}

export default useEmrSections
